export default function (Alpine) {

    function createElement(tag, props = {}, children = []) {
        const el = document.createElement(tag);
        for (const [key, value] of Object.entries(props)) {
            if (key === 'class' || key === 'className') el.className = value;
            else if (key.startsWith('on') && typeof value === 'function')
                el.addEventListener(key.slice(2).toLowerCase(), value);
            else el.setAttribute(key, value);
        }
        children.forEach(child =>
            el.appendChild(typeof child === 'string' ? document.createTextNode(child) : child)
        );
        return el;
    }

    const _openSelects = new Set();

    Alpine.data('select', () => ({
        open:             false,
        options:          [],
        groups:           [],
        selectedIndex:    -1,
        highlightedIndex: -1,
        searchQuery:      '',
        disabled:         false,
        multiple:         false,
        selectedIndices:  [],
        maxSelected:      Infinity,

        searchable:  false,
        combobox:    false,
        dropdown:    false,
        virtualScroll: false,

        input:          null,
        _flatOptions:   [],
        _classes:       {},
        _textEl:        null,
        _optionsEl:     null,
        _hasPlaceholder: false,
        _initialSelectedIndex:   -1,
        _initialSelectedIndices: [],

        _optionListeners: [],
        _outsideHandler:  null,

        _observer:      null,
        _observerTimer: null,

        _formResetHandler: null,
        _form:             null,

        _teleport:        false,
        _teleportEl:      null,
        _positionHandler: null,

        _comboboxEl:      null,
        _comboboxTagsEl:  null,

        virtualItemHeight:   36,
        virtualVisibleCount: 8,
        virtualOffset:       0,

        _composing: false,


        init() {
            const root = this.$root;
            const self = this;

            self.searchable      = root.hasAttribute('searchable');
            self.multiple        = root.hasAttribute('multiple');
            self.disabled        = root.hasAttribute('disabled');
            self.virtualScroll   = root.hasAttribute('virtual');
            self._hasPlaceholder = root.hasAttribute('placeholder');
            self._teleport       = root.hasAttribute('teleport');
            self.combobox        = root.hasAttribute('combobox');
            self.dropdown        = root.hasAttribute('dropdown');
            self.placeholder     = root.getAttribute('placeholder') ?? 'Seçiniz...';
            self.maxSelected     = root.hasAttribute('max-selected')
                ? parseInt(root.getAttribute('max-selected'), 10)
                : Infinity;
            const name = root.getAttribute('data-name') ?? root.getAttribute('name') ?? 'select';

            // Özel class'lar
            self._classes = {
                trigger:     root.getAttribute('class-trigger')     ?? '',
                option:      root.getAttribute('class-option')      ?? '',
                selected:    root.getAttribute('class-selected')    ?? '',
                highlighted: root.getAttribute('class-highlighted') ?? '',
                disabled:    root.getAttribute('class-disabled')    ?? '',
                groupLabel:  root.getAttribute('class-group-label') ?? '',
                search:      root.getAttribute('class-search')      ?? '',
                badge:       root.getAttribute('class-badge')       ?? '',
            };

            // Gizli <select> — dropdown modda gerekmez
            if (!self.dropdown) {
                self.input = createElement('select', { name });
                Object.assign(self.input.style, {
                    position: 'absolute', width: '1px', height: '1px',
                    opacity: '0', pointerEvents: 'none',
                });
                self.input.setAttribute('tabindex', '-1');
                self.input.setAttribute('aria-hidden', 'true');
                if (self.multiple)                 self.input.setAttribute('multiple', '');
                if (root.hasAttribute('required')) self.input.setAttribute('required', '');
                if (root.hasAttribute('form'))     self.input.setAttribute('form', root.getAttribute('form'));
                root.appendChild(self.input);
            }

            self._textEl    = root.querySelector('.placeholder .text');
            self._optionsEl = root.querySelector('.options');

            // ARIA: options listesi
            if (self._optionsEl) {
                self._optionsEl.setAttribute('role', self.dropdown ? 'menu' : 'listbox');
                if (self.multiple) self._optionsEl.setAttribute('aria-multiselectable', 'true');
                if (!self._optionsEl.id) self._optionsEl.id = `select-options-${Math.random().toString(36).slice(2, 8)}`;
            }

            if (self._teleport && self._optionsEl) self._initTeleport();

            self._syncRootAttrs();

            // Option'ları parse et
            const groupEls = [...root.querySelectorAll('.option-group')];
            if (groupEls.length) {
                self._initGroups(groupEls);
            } else {
                self._initOptions([...root.querySelectorAll('.option')]);
            }

            // Trigger bağla
            const triggerEl = root.querySelector('.placeholder');
            if (triggerEl) {
                if (self._classes.trigger)
                    triggerEl.classList.add(...self._classes.trigger.split(' ').filter(Boolean));
                self._bindTrigger(triggerEl);
            }

            // Mod-spesifik init
            if (self.combobox) self._initCombobox();
            if (self.searchable && !self.combobox) self._initSearch();

            // Dışarı tıklamayla kapat
            self._outsideHandler = (e) => {
                const inRoot     = root.contains(e.target);
                const inTeleport = self._teleportEl?.contains(e.target);
                if (!inRoot && !inTeleport) self.close();
            };
            document.addEventListener('mousedown', self._outsideHandler);

            self._initMutationObserver();
            self._initFormReset();
            self._watch();
        },


        destroy() {
            const self = this;

            document.removeEventListener('mousedown', self._outsideHandler);
            _openSelects.delete(self);

            self._observer?.disconnect();
            self._observer = null;

            if (self._observerTimer) {
                clearTimeout(self._observerTimer);
                self._observerTimer = null;
            }

            if (self._formResetHandler && self._form) {
                self._form.removeEventListener('reset', self._formResetHandler);
                self._formResetHandler = null;
                self._form = null;
            }

            self._optionListeners.forEach(({ el, type, handler }) =>
                el.removeEventListener(type, handler)
            );
            self._optionListeners = [];

            self._comboboxEl     = null;
            self._comboboxTagsEl = null;

            if (self._teleportEl) {
                if (self._positionHandler) {
                    window.removeEventListener('resize', self._positionHandler);
                    window.removeEventListener('scroll', self._positionHandler, true);
                    self._positionHandler = null;
                }
                self._teleportEl.remove();
                self._teleportEl = null;
            }
        },


        _initTeleport() {
            const self = this;
            self._teleportEl = self._optionsEl;
            document.body.appendChild(self._teleportEl);
            Object.assign(self._teleportEl.style, { position: 'fixed', zIndex: '9999' });
            self._positionHandler = () => { if (self.open) self._positionDropdown(); };
            window.addEventListener('resize', self._positionHandler);
            window.addEventListener('scroll', self._positionHandler, true);
        },

        _positionDropdown() {
            const self = this;
            if (!self._teleportEl) return;
            const triggerEl = self.$root.querySelector('.placeholder');
            if (!triggerEl) return;
            const tr = triggerEl.getBoundingClientRect();
            const dr = self._teleportEl.getBoundingClientRect();
            const vH = window.innerHeight, vW = window.innerWidth, gap = 6;
            const spaceBelow = vH - tr.bottom - gap;
            const spaceAbove = tr.top - gap;
            const dropH = dr.height || 200;
            let top;
            if (spaceBelow >= dropH || spaceBelow >= spaceAbove) {
                top = tr.bottom + gap;
                self._teleportEl.removeAttribute('flip');
            } else {
                top = tr.top - dropH - gap;
                self._teleportEl.setAttribute('flip', '');
            }
            let left = tr.left;
            const dropW = dr.width || tr.width;
            if (left + dropW > vW) left = tr.right - dropW;
            if (left < 0) left = gap;
            self._teleportEl.style.top  = `${top}px`;
            self._teleportEl.style.left = `${left}px`;
        },

        _updateFlip() {
            const self = this;
            if (self._teleport) { self._positionDropdown(); return; }
            const optionsEl = self._optionsEl;
            if (!optionsEl) return;
            const triggerEl = self.$root.querySelector('.placeholder');
            if (!triggerEl) return;
            const tr = triggerEl.getBoundingClientRect();
            const dr = optionsEl.getBoundingClientRect();
            const vH = window.innerHeight, gap = 6;
            const spaceBelow = vH - tr.bottom - gap;
            const spaceAbove = tr.top - gap;
            const dropH = dr.height || 200;
            if (spaceBelow < dropH && spaceAbove > spaceBelow) {
                self.$root.setAttribute('flip', '');
            } else {
                self.$root.removeAttribute('flip');
            }
        },


        _initCombobox() {
            const self = this;
            const root = self.$root;
            const placeholderEl = root.querySelector('.placeholder');
            const input = placeholderEl?.querySelector('input.combobox-input');

            if (!input) {
                console.warn('[select] combobox mode: .placeholder içinde <input class="combobox-input"> gerekli.');
                return;
            }

            self._comboboxEl = input;
            input.setAttribute('role', 'combobox');
            input.setAttribute('aria-autocomplete', 'list');
            input.setAttribute('aria-expanded', 'false');
            if (self._optionsEl?.id) input.setAttribute('aria-controls', self._optionsEl.id);
            if (self.disabled) input.disabled = true;

            // Multiple: tag container
            if (self.multiple) {
                self._comboboxTagsEl = placeholderEl.querySelector('.combobox-tags') ?? (() => {
                    const el = document.createElement('div');
                    el.className = 'combobox-tags';
                    el.setAttribute('aria-live', 'polite');
                    input.insertAdjacentElement('beforebegin', el);
                    return el;
                })();
            }

            // Arrow tıklaması
            const arrow = placeholderEl.querySelector('.combobox-arrow, .arrow, .select-arrow');
            arrow?.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!self.disabled) self.toggle();
                else input.focus();
            });

            // IME desteği
            input.addEventListener('compositionstart', () => { self._composing = true; });
            input.addEventListener('compositionend',   (e) => {
                self._composing = false;
                self.searchQuery = e.target.value;
                self._filterDOM();
                if (!self.open) self._open();
            });

            input.addEventListener('input', (e) => {
                if (self._composing) return;
                self.searchQuery = e.target.value;
                self._filterDOM();
                if (!self.open) self._open();
            });

            input.addEventListener('focus', () => {
                if (!self.disabled && !self.open) self._open();
            });

            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const first = self._flatOptions.find(o =>
                        !o.element.classList.contains('no-match') && !o.disabled
                    );
                    if (first) self.selectOption(first.index);
                }
                if (e.key === 'Escape')    { self.close(); input.blur(); }
                if (e.key === 'Tab')       { self.close(); }
                if (e.key === 'Home')      { e.preventDefault(); self._moveHighlightTo('first'); }
                if (e.key === 'End')       { e.preventDefault(); self._moveHighlightTo('last'); }
                if (e.key === 'ArrowDown') { e.preventDefault(); self._moveHighlight(1); }
                if (e.key === 'ArrowUp')   { e.preventDefault(); self._moveHighlight(-1); }
                if (e.key === 'Backspace' && !input.value && self.multiple && self.selectedIndices.length) {
                    const lastIdx = self.selectedIndices[self.selectedIndices.length - 1];
                    self.selectOption(lastIdx);
                }
            });

            placeholderEl.addEventListener('mousedown', (e) => {
                if (e.target !== input) { e.preventDefault(); input.focus(); }
            });

            // open watch: aria-expanded + input sıfırlama
            self.$watch('open', (val) => {
                input.setAttribute('aria-expanded', val ? 'true' : 'false');
                root.toggleAttribute('open', val);
                if (self._teleportEl) self._teleportEl.toggleAttribute('open', val);
                if (!val) { self._updateComboboxInput(); self._clearSearch(); }
                else { self.$nextTick(() => { input.focus(); }); }
            });

            self.$watch('disabled', (val) => {
                input.disabled = val;
                root.toggleAttribute('disabled', val);
                if (self.input) self.input.disabled = val;
            });

            self._updateComboboxDisplay();
        },

        _updateComboboxInput() {
            const self = this;
            if (!self._comboboxEl) return;
            if (self.multiple) {
                self._comboboxEl.value = '';
            } else {
                const s = self.selected();
                self._comboboxEl.value = s ? (s.label ?? s.value ?? '') : '';
            }
        },

        _updateComboboxDisplay() {
            const self = this;
            if (!self.combobox) return;
            self._updateComboboxInput();
            if (self.multiple) self._renderTags();
        },

        _renderTags() {
            const self = this;
            if (!self._comboboxTagsEl) return;
            self._comboboxTagsEl.innerHTML = '';
            self.selectedIndices.forEach(i => {
                const opt = self._flatOptions[i];
                if (!opt) return;
                const tag = document.createElement('span');
                tag.className = 'combobox-tag';
                tag.textContent = opt.label ?? opt.value ?? '';
                const removeBtn = document.createElement('button');
                removeBtn.type = 'button';
                removeBtn.className = 'combobox-tag-remove';
                removeBtn.innerHTML = '&times;';
                removeBtn.setAttribute('aria-label', `${opt.label ?? opt.value} seçimini kaldır`);
                removeBtn.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    self.selectOption(i);
                    self._comboboxEl?.focus();
                });
                tag.appendChild(removeBtn);
                self._comboboxTagsEl.appendChild(tag);
            });
        },


        _initSearch() {
            const self = this;
            const searchEl = self.$root.querySelector('input.search');
            if (!searchEl) return;

            searchEl.addEventListener('compositionstart', () => { self._composing = true; });
            searchEl.addEventListener('compositionend',   (e) => {
                self._composing = false;
                self.searchQuery = e.target.value;
                self._filterDOM();
            });

            searchEl.addEventListener('input', (e) => {
                if (self._composing) return;
                self.searchQuery = e.target.value;
                self._filterDOM();
            });

            self.$watch('open', (val) => {
                if (val) self.$nextTick(() => searchEl.focus());
                else self._clearSearch();
            });

            searchEl.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.stopPropagation();
                    const first = self._flatOptions.find(o =>
                        !o.element.classList.contains('no-match') && !o.disabled
                    );
                    if (first) self.selectOption(first.index);
                }
                if (e.key === 'Escape')    self.close();
                if (e.key === 'Home')      { e.preventDefault(); self._moveHighlightTo('first'); }
                if (e.key === 'End')       { e.preventDefault(); self._moveHighlightTo('last'); }
                if (e.key === 'ArrowDown') { e.preventDefault(); self._moveHighlight(1); }
                if (e.key === 'ArrowUp')   { e.preventDefault(); self._moveHighlight(-1); }
            });

            searchEl.addEventListener('mousedown', (e) => e.stopPropagation());
        },

        _clearSearch() {
            const self = this;
            const searchEl = self.combobox
                ? self._comboboxEl
                : self.$root.querySelector('input.search');
            if (searchEl) searchEl.value = '';
            self.searchQuery = '';
            self._filterDOM();
        },

        _filterDOM() {
            const self = this;
            const q = self.searchQuery.toLowerCase().trim();
            self._flatOptions.forEach(opt => {
                const label = (opt.label ?? opt.value ?? '').toLowerCase();
                opt.element.classList.toggle('no-match', !!q && !label.includes(q));
            });
            self.groups.forEach(group => {
                const hasMatch = [...group.element.querySelectorAll('.option')]
                    .some(el => !el.classList.contains('no-match'));
                group.element.classList.toggle('no-match', !hasMatch);
            });
            const firstMatch = self._flatOptions.findIndex(o =>
                !o.element.classList.contains('no-match') && !o.disabled
            );
            self.highlightedIndex = firstMatch;
        },


        _initMutationObserver() {
            const self   = this;
            const target = self._optionsEl ?? self.$root;

            self._observer = new MutationObserver((mutations) => {
                const hasRelevantChange = mutations.some(m =>
                    m.type === 'childList' ||
                    (m.type === 'attributes' && m.attributeName === 'disabled')
                );
                if (!hasRelevantChange) return;

                clearTimeout(self._observerTimer);
                self._observerTimer = setTimeout(() => {
                    const prevValues = self.multiple
                        ? self.selectedIndices.map(i => self._flatOptions[i]?.value).filter(Boolean)
                        : [self._flatOptions[self.selectedIndex]?.value].filter(Boolean);

                    const groupEls = [...self.$root.querySelectorAll('.option-group')];
                    if (groupEls.length) {
                        self._initGroups(groupEls);
                    } else {
                        self._initOptions([...(self._optionsEl ?? self.$root).querySelectorAll('.option')]);
                    }

                    // Search bağlantısını yenile
                    if (self.searchable && !self.combobox) self._initSearch();

                    if (prevValues.length) {
                        self.setValue(self.multiple ? prevValues : prevValues[0]);
                    }
                }, 50);
            });

            self._observer.observe(target, {
                childList: true,
                subtree:   true,       // alt element disabled değişikliği için
                attributes: true,
                attributeFilter: ['disabled'],
            });
        },


        _initFormReset() {
            const self = this;
            const form = self.$root.closest('form');
            if (!form) return;
            self._form = form;
            self._formResetHandler = () => self.reset();
            form.addEventListener('reset', self._formResetHandler);
        },


        setOptions(newOptions = []) {
            const self   = this;
            const target = self._optionsEl ?? self.$root;
            target.querySelectorAll('.option').forEach(el => el.remove());
            const elements = newOptions.map(opt => {
                const el = document.createElement('div');
                el.className = 'option';
                if (opt.value       !== undefined) el.setAttribute('value', opt.value);
                if (opt.label       !== undefined) el.setAttribute('label', opt.label);
                if (opt.description !== undefined) el.setAttribute('data-description', opt.description);
                if (opt.disabled)                  el.setAttribute('disabled', '');
                if (opt.selected)                  el.setAttribute('selected', '');
                el.textContent = opt.label ?? opt.value ?? '';
                target.appendChild(el);
                return el;
            });
            self.selectedIndex   = -1;
            self.selectedIndices = [];
            self.highlightedIndex = -1;
            self._initOptions(elements);
            self._initialSelectedIndex   = self.selectedIndex;
            self._initialSelectedIndices = [...self.selectedIndices];
        },

        setValue(value) {
            const self = this;
            if (self.multiple) {
                const values = Array.isArray(value) ? value : [value];
                self.selectedIndices = [];
                values.forEach(v => {
                    const idx = self._flatOptions.findIndex(o => o.value === v);
                    if (idx !== -1 && !self._flatOptions[idx].disabled &&
                        self.selectedIndices.length < self.maxSelected) {
                        self.selectedIndices = [...self.selectedIndices, idx];
                    }
                });
                self._flatOptions = self._flatOptions.map((item, i) => ({
                    ...item, selected: self.selectedIndices.includes(i),
                }));
                self.options = self._flatOptions;
            } else {
                const idx = self._flatOptions.findIndex(o => o.value === value);
                if (idx !== -1) self.setSelected(idx);
            }
            self._syncInput();
            self._updateText();
            self._updateRootAttrs();
            self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
            self.$root.dispatchEvent(new CustomEvent('change', {
                detail: { value: self._currentValue(), selected: self.selected() },
                bubbles: true,
            }));
        },

        reset() {
            const self = this;
            if (self.multiple) {
                self.selectedIndices = [...self._initialSelectedIndices];
                self._flatOptions = self._flatOptions.map((item, i) => ({
                    ...item, selected: self.selectedIndices.includes(i),
                }));
                self.options = self._flatOptions;
            } else {
                if (self._initialSelectedIndex !== -1) {
                    self._flatOptions = self._flatOptions.map((item, i) => ({
                        ...item, selected: i === self._initialSelectedIndex,
                    }));
                    self.options       = self._flatOptions;
                    self.selectedIndex = self._initialSelectedIndex;
                } else {
                    self.selectedIndex = -1;
                    self._flatOptions  = self._flatOptions.map(o => ({ ...o, selected: false }));
                    self.options       = self._flatOptions;
                }
            }
            self._syncInput(true);
            self._updateText();
            self._updateRootAttrs();
            self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
            self.$root.dispatchEvent(new CustomEvent('reset', {
                detail: { value: self._currentValue(), selected: self.selected() },
                bubbles: true,
            }));
        },


        _updateText() {
            const self = this;
            if (self.combobox) { self._updateComboboxDisplay(); return; }
            if (!self._textEl) return;
            if (self.multiple && self.placeholder.includes('[]')) {
                const count = self.selectedIndices.length;
                self._textEl.innerHTML = self.placeholder.replace('[]', count === 0 ? '' : String(count));
            } else {
                self._textEl.textContent = self.selectedLabel();
            }
        },

        selectedLabel() {
            const self = this;
            if (self.multiple) {
                if (self.placeholder.includes('[]')) {
                    const count = self.selectedIndices.length;
                    return self.placeholder.replace('[]', count === 0 ? '' : String(count));
                }
                return self.placeholder;
            }
            const s = self.selected();
            return s ? (s.label ?? s.value) : self.placeholder;
        },


        _syncRootAttrs() {
            const root = this.$root;
            root.setAttribute('data-select', '');
            root.toggleAttribute('open',     this.open);
            root.toggleAttribute('disabled', this.disabled);
            root.toggleAttribute('multiple', this.multiple);
        },

        _updateRootAttrs() {
            const self = this, root = self.$root;
            root.toggleAttribute('open', self.open);
            root.toggleAttribute('has-value',
                self.multiple ? self.selectedIndices.length > 0 : !!self.selected()
            );
            root.setAttribute('selected-count',
                self.multiple ? self.selectedIndices.length : (self.selectedIndex >= 0 ? 1 : 0)
            );
        },


        _syncOptionAttr(el, index) {
            const isSelected    = this.isSelected(index);
            const isHighlighted = this.highlightedIndex === index;
            el.toggleAttribute('selected',    isSelected);
            el.toggleAttribute('highlighted', isHighlighted);
            if (!this.dropdown) {
                el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            }
            if (isHighlighted) {
                el.setAttribute('id', el.id || `select-opt-${index}`);
                const ctrl = this._comboboxEl ?? this.$root.querySelector('.placeholder');
                ctrl?.setAttribute('aria-activedescendant', el.id);
                el.scrollIntoView({ block: 'nearest' });
            }
        },


        _syncInput(silent = false) {
            const self = this;
            if (!self.input) return;
            self.input.innerHTML = '';
            self._flatOptions.forEach(opt => {
                const el = document.createElement('option');
                el.value    = opt.value ?? '';
                el.text     = opt.label ?? opt.value ?? '';
                el.selected = self.isSelected(opt.index);
                if (opt.disabled) el.disabled = true;
                self.input.appendChild(el);
            });
            if (!silent) self.input.dispatchEvent(new Event('change', { bubbles: true }));
        },

        _currentValue() {
            const self = this;
            if (self.multiple)
                return self.selectedIndices.map(i => self._flatOptions[i]?.value).filter(Boolean);
            return self._flatOptions[self.selectedIndex]?.value ?? '';
        },


        _initGroups(groupEls) {
            const self = this;
            self.groups = groupEls.map(groupEl => ({
                label:   groupEl.getAttribute('label') ?? '',
                element: groupEl,
            }));
            groupEls.forEach(groupEl => {
                const labelEl = groupEl.querySelector('.group-label');
                if (labelEl && self._classes.groupLabel)
                    labelEl.classList.add(...self._classes.groupLabel.split(' ').filter(Boolean));
            });
            self._initOptions(groupEls.flatMap(g => [...g.querySelectorAll('.option')]));
        },

        optionsForGroup(groupEl) {
            const self = this;
            const groupOptions = self._flatOptions.filter(o => groupEl.contains(o.element));
            if (!self.searchable || !self.searchQuery) return groupOptions;
            const q = self.searchQuery.toLowerCase();
            return groupOptions.filter(o =>
                (o.label ?? o.value ?? '').toLowerCase().includes(q)
            );
        },

        groupHasResults(groupEl) {
            return this.optionsForGroup(groupEl).length > 0;
        },


        _initOptions(elements) {
            const self = this;

            self._optionListeners.forEach(({ el, type, handler }) =>
                el.removeEventListener(type, handler)
            );
            self._optionListeners = [];

            self._flatOptions = elements.map((el, index) => {
                const option = {
                    value:       el.getAttribute('value')            ?? null,
                    label:       el.getAttribute('label')            ?? el.textContent.trim(),
                    description: el.getAttribute('data-description') ?? null,
                    disabled:    el.hasAttribute('disabled'),
                    selected:    el.hasAttribute('selected'),
                    element: el,
                    index,
                };

                el.setAttribute('data-option', '');
                el.setAttribute('role', self.dropdown ? 'menuitem' : 'option');
                el.setAttribute('id', `select-opt-${Math.random().toString(36).slice(2, 7)}`);
                el.toggleAttribute('disabled', option.disabled);
                if (option.disabled) el.setAttribute('aria-disabled', 'true');
                if (option.description) el.setAttribute('data-description', option.description);
                if (self._classes.option)
                    el.classList.add(...self._classes.option.split(' ').filter(Boolean));

                const clickHandler = (e) => {
                    e.stopPropagation();
                    if (self.dropdown) {
                        // Dropdown: state tutma, sadece kapat
                        if (!option.disabled) self.close();
                    } else {
                        self.selectOption(index);
                    }
                };
                const enterHandler = () => {
                    if (!option.disabled) self.highlightedIndex = index;
                };

                el.addEventListener('click',      clickHandler);
                el.addEventListener('mouseenter', enterHandler);
                self._optionListeners.push(
                    { el, type: 'click',      handler: clickHandler },
                    { el, type: 'mouseenter', handler: enterHandler }
                );

                return option;
            });

            self.options = self._flatOptions;

            // Dropdown modda state yönetimi gerekmez
            if (self.dropdown) {
                self._updateRootAttrs();
                return;
            }

            if (self.multiple) {
                self.selectedIndices = self._flatOptions
                    .map((o, i) => o.selected ? i : -1)
                    .filter(i => i !== -1);
                self._flatOptions = self._flatOptions.map((item, i) => ({
                    ...item, selected: self.selectedIndices.includes(i),
                }));
                self.options = self._flatOptions;
                self._initialSelectedIndices = [...self.selectedIndices];
            } else {
                let idx = self._flatOptions.findIndex(o => o.selected);
                if (idx === -1 && !self._hasPlaceholder)
                    idx = self._flatOptions.findIndex(o => !o.disabled);
                if (idx !== -1) {
                    self._flatOptions = self._flatOptions.map((item, i) => ({
                        ...item, selected: i === idx,
                    }));
                    self.options       = self._flatOptions;
                    self.selectedIndex = idx;
                }
                self._initialSelectedIndex = self.selectedIndex;
            }

            self._syncInput(true);
            self._updateText();
            self._updateRootAttrs();
            self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
        },


        get virtualTotalHeight() {
            return this.filteredOptions().length * this.virtualItemHeight;
        },

        get virtualItems() {
            const self = this;
            if (!self.virtualScroll) return self.filteredOptions();
            const start = Math.floor(self.virtualOffset / self.virtualItemHeight);
            const end   = start + self.virtualVisibleCount + 1;
            return self.filteredOptions().slice(start, end).map((opt, i) => ({
                ...opt, _top: (start + i) * self.virtualItemHeight,
            }));
        },

        onVirtualScroll(e) {
            this.virtualOffset = e.target.scrollTop;
        },


        selected() {
            const self = this;
            if (self.multiple)
                return self.selectedIndices.map(i => self._flatOptions[i]).filter(Boolean);
            return self._flatOptions[self.selectedIndex] ?? null;
        },

        filteredOptions() {
            const self = this;
            if (!self.searchable || !self.searchQuery) return self._flatOptions;
            const q = self.searchQuery.toLowerCase();
            return self._flatOptions.filter(o =>
                (o.label ?? o.value ?? '').toLowerCase().includes(q)
            );
        },

        isSelected(index) {
            return this.multiple
                ? this.selectedIndices.includes(index)
                : this.selectedIndex === index;
        },

        get canSelectMore() {
            return !this.multiple || this.selectedIndices.length < this.maxSelected;
        },

        optionClass(index) {
            const self = this;
            const { option: base, selected: sel, highlighted: hl, disabled: dis } = self._classes;
            const opt = self._flatOptions[index];
            if (opt?.disabled)                   return `${base} ${dis}`.trim();
            if (self.isSelected(index))          return `${base} ${sel}`.trim();
            if (self.highlightedIndex === index) return `${base} ${hl}`.trim();
            return base;
        },


        toggle() {
            this.open ? this.close() : this._open();
        },

        _open() {
            const self = this;

            _openSelects.forEach(other => {
                if (other !== self) other.close();
            });

            self.open = true;
            _openSelects.add(self);

            self.highlightedIndex = self.selectedIndex >= 0 ? self.selectedIndex : 0;
            self.searchQuery   = '';
            self.virtualOffset = 0;
            self._updateRootAttrs();
            self.$nextTick(() => self._updateFlip());
        },

        close() {
            if (!this.open) return;
            this.open = false;
            _openSelects.delete(this);
            this.highlightedIndex = -1;
            this._updateRootAttrs();
            this.$root.removeAttribute('flip');
            if (this._teleportEl) this._teleportEl.removeAttribute('flip');
            const ctrl = this._comboboxEl ?? this.$root.querySelector('.placeholder');
            ctrl?.removeAttribute('aria-activedescendant');
        },


        selectOption(index) {
            const self = this;
            if (index < 0 || index >= self._flatOptions.length) return;
            if (self._flatOptions[index]?.disabled) return;

            if (self.multiple) {
                const pos = self.selectedIndices.indexOf(index);
                if (pos === -1) {
                    if (!self.canSelectMore) return;
                    self.selectedIndices = [...self.selectedIndices, index];
                } else {
                    self.selectedIndices = self.selectedIndices.filter(i => i !== index);
                }
                self._flatOptions = self._flatOptions.map((item, i) => ({
                    ...item, selected: self.selectedIndices.includes(i),
                }));
                self.options = self._flatOptions;
                self._syncInput();
                self._updateText();
                self._updateRootAttrs();
                self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
                if (self.combobox && self._comboboxEl)
                    self.$nextTick(() => self._comboboxEl.focus());
                self.$root.dispatchEvent(new CustomEvent('change', {
                    detail: { value: self._currentValue(), selected: self.selected() },
                    bubbles: true,
                }));
                return;
            }

            // Single
            if (self.searchable || self.combobox) self._clearSearch();

            self.setSelected(index);
            self.close();

            if (self.combobox && self._comboboxEl)
                self.$nextTick(() => self._comboboxEl.blur());

            self._syncInput();
            self._updateText();
            self._updateRootAttrs();
            self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
            self.$root.dispatchEvent(new CustomEvent('change', {
                detail: { value: self._currentValue(), selected: self.selected() },
                bubbles: true,
            }));
        },

        setSelected(index) {
            const self = this;
            if (index < 0 || index >= self._flatOptions.length) return;
            self._flatOptions  = self._flatOptions.map((item, i) => ({ ...item, selected: i === index }));
            self.options       = self._flatOptions;
            self.selectedIndex = index;
            self._syncInput(true);
            self._updateText();
        },

        clear() {
            const self = this;
            if (self.multiple) {
                self.selectedIndices = [];
                self._flatOptions = self._flatOptions.map(o => ({ ...o, selected: false }));
                self.options = self._flatOptions;
            } else {
                self.selectedIndex = -1;
                self._flatOptions  = self._flatOptions.map(o => ({ ...o, selected: false }));
                self.options = self._flatOptions;
            }
            self._syncInput();
            self._updateText();
            self._updateRootAttrs();
            self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
            self.$root.dispatchEvent(new CustomEvent('change', {
                detail: { value: self._currentValue(), selected: self.selected() },
                bubbles: true,
            }));
        },


        _bindTrigger(el) {
            const self = this;

            if (self.combobox) return;

            const role = self.dropdown ? 'button' : 'combobox';
            el.setAttribute('role', role);
            el.setAttribute('tabindex', self.disabled ? '-1' : '0');
            if (!self.dropdown) {
                el.setAttribute('aria-haspopup', 'listbox');
                el.setAttribute('aria-expanded', 'false');
                if (self._optionsEl?.id) el.setAttribute('aria-controls', self._optionsEl.id);
            }

            el.addEventListener('click', (e) => {
                e.stopPropagation();
                if (!self.disabled) self.toggle();
            });

            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!self.disabled) self.toggle();
                }
                if (e.key === 'Escape')    self.close();
                if (e.key === 'Tab')       self.close();
                if (e.key === 'Home')      { e.preventDefault(); self._moveHighlightTo('first'); }
                if (e.key === 'End')       { e.preventDefault(); self._moveHighlightTo('last'); }
                if (e.key === 'ArrowDown') { e.preventDefault(); self._moveHighlight(1); }
                if (e.key === 'ArrowUp')   { e.preventDefault(); self._moveHighlight(-1); }
            });

            self.$watch('open', (val) => {
                el.setAttribute('aria-expanded', val ? 'true' : 'false');
                self.$root.toggleAttribute('open', val);
                if (self._teleportEl) self._teleportEl.toggleAttribute('open', val);
            });

            self.$watch('disabled', (val) => {
                el.setAttribute('tabindex', val ? '-1' : '0');
                self.$root.toggleAttribute('disabled', val);
                if (self.input) self.input.disabled = val;
            });
        },


        _moveHighlight(dir) {
            const self = this;
            const opts = self._flatOptions.filter(o =>
                !o.disabled && !o.element.classList.contains('no-match')
            );
            if (!opts.length) return;
            const currentPos = opts.findIndex(o => o.index === self.highlightedIndex);
            const nextPos    = Math.max(0, Math.min(currentPos + dir, opts.length - 1));
            self.highlightedIndex = opts[nextPos].index;
        },

        _moveHighlightTo(target) {
            const self = this;
            const opts = self._flatOptions.filter(o =>
                !o.disabled && !o.element.classList.contains('no-match')
            );
            if (!opts.length) return;
            self.highlightedIndex = target === 'first'
                ? opts[0].index
                : opts[opts.length - 1].index;
        },


        _watch() {
            const self = this;

            self.$watch('open', (val) => {
                self.$root.dispatchEvent(
                    new CustomEvent(val ? 'open' : 'close', { bubbles: true })
                );
            });

            self.$watch('searchQuery', () => {
                self.highlightedIndex = 0;
                self.virtualOffset    = 0;
            });

            self.$watch('highlightedIndex', () => {
                self._flatOptions.forEach((o, i) => self._syncOptionAttr(o.element, i));
            });
        },

    }));

    /*
    *
			<div class="select" multiple searchable placeholder="Socials <small>[]</small>" x-data="select" name="socials">
				<div class="placeholder">
					<span class="text"></span>
					<span class="select-arrow">
		                <i class="fa-solid fa-chevron-down"></i>
		            </span>
				</div>
				<div class="options">
					<input class="search" placeholder="Ara..">
					<div class="option" value="instagram">
						<i class="fa-solid fa-check"></i> Instagram
					</div>
					<div class="option" value="facebook">
						<i class="fa-solid fa-check"></i> Facebook
					</div>
					<div class="option" value="tiktok" disabled>
						<i class="fa-solid fa-check"></i> Tiktok
					</div>
				</div>
			</div>
    * */
}